import {
  availableMetric,
  isMachine,
  isPending,
  pendingMetric,
  type MetricValue,
} from '@fleetcore/shared'
import type { LiveVehicleState } from '@fleetcore/shared/telemetry'
import { DoorOpen, KeyRound, Zap } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ArcGauge } from '../../components/gauges/arc-gauge'
import { LevelGauge } from '../../components/gauges/level-gauge'
import { usePendingLabel } from '../../components/gauges/pending-label'
import { SignalGauge } from '../../components/gauges/signal-gauge'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader } from '../../components/ui/card'
import { useFormatters } from '../../i18n/format'
import { cn } from '../../lib/cn'
import { litresFromPercent, measuredFuelLitres, reportedFuelPercent } from '../../lib/fuel-litres'
import type { CapabilityProfile, LiveVehicleEnvelope, VehicleView } from './api-types'
import { capabilityReading, type ChannelReading } from './capability-model'
import { CanUnavailableNotice } from './capability-notice'
import { setBits } from './raw-channels'
import { useChannelNotice } from './use-channel-notice'
import { channelReading } from './usage-metrics'

/**
 * The live telemetry drawer — what this vehicle is doing right now.
 *
 * Everything here reads the Redis projection through `GET /live/vehicles/:id`,
 * never the hypertable (invariant 1). `connectivity` on the envelope is computed
 * at read time by the API and is the only thing in this file that may say
 * "offline"; `movementState` never does, by construction (invariant 2).
 *
 * CAN-only readings — RPM, fuel level, doors, control-module voltage — are the
 * reason this panel is written around explained absence instead of conditional
 * rendering. A vehicle with no adapter shows the same dials with the sentence that
 * names what to check, so that "this truck has no CAN harness" and "the panel is
 * broken" never look alike.
 */

/** Dial ceilings. A 220 km/h scale on a 20 km/h excavator wastes the whole arc. */
function speedScaleFor(vehicle: VehicleView): number {
  if (isMachine(vehicle)) return 40
  return vehicle.vehicleClass === 'truck' ? 130 : 180
}

/** Diesel redlines near 4 000 rpm; a petrol car does not. Fuel type is the tell. */
function rpmScaleFor(vehicle: VehicleView): number {
  return vehicle.fuelType === 'gasoline' || vehicle.fuelType === 'hybrid' ? 8000 : 4000
}

/**
 * The coolant dial's scale.
 *
 * It starts BELOW zero and it must: a cold-start reading of −8 °C on a winter morning
 * is a real measurement, and a dial whose floor is 0 would peg it and report a warm
 * engine. The redline sits at 105 °C, where a fleet diesel is genuinely in trouble.
 */
const TEMP_MIN = -40
const TEMP_MAX = 130
const TEMP_REDLINE = (105 - TEMP_MIN) / (TEMP_MAX - TEMP_MIN)

/** Satellites, mapped onto five bars. Four is the minimum for a 3D fix at all. */
function satelliteBars(count: number): number {
  if (count <= 0) return 0
  if (count < 4) return 1
  if (count < 6) return 2
  if (count < 9) return 3
  if (count < 12) return 4
  return 5
}

export interface LiveTelemetryDrawerProps {
  vehicle: VehicleView
  live?: LiveVehicleEnvelope
  capabilities?: CapabilityProfile
  isLoading?: boolean
  className?: string
}

export function LiveTelemetryDrawer({
  vehicle,
  live,
  capabilities,
  isLoading = false,
  className,
}: LiveTelemetryDrawerProps) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const notice = useChannelNotice(capabilities)
  const state = live?.state ?? null
  const canAvailable = capabilities?.canBus.available ?? false

  const speed = speedMetric(state)
  const rpm = rpmReading(state, capabilities)
  // Coolant first, engine block second: an ALL-CAN300 publishes coolant on most
  // programs and engine temperature on few, and reading the one that arrived beats
  // showing an empty dial next to a full one.
  const temperature = temperatureReading(state, capabilities)
  // CAN first; the OBD reading of the same tank otherwise — see rpmReading.
  const fuelPct = reportedFuelPercent(
    state?.fuelLevelPct ?? state?.channels?.obd_fuel_level_pct ?? null,
    state?.fuelLevelL ?? null,
  )
  // Litres as measured; else percent × the tank size on the vehicle record, as "≈".
  const measuredL = measuredFuelLitres(state?.fuelLevelL ?? null, fuelPct)
  const derivedL = measuredL === null ? litresFromPercent(fuelPct, vehicle.tankCapacityL) : null
  const fuelLitresText =
    measuredL !== null
      ? `${formatters.decimal(measuredL, 1)} L`
      : derivedL !== null
        ? `≈ ${formatters.decimal(derivedL, 0)} L`
        : null

  return (
    <Card className={cn('overflow-hidden', className)}>
      <CardHeader
        title={t('vehicles:detail.live.title')}
        description={
          state === null
            ? t('vehicles:detail.live.noStateDescription')
            : t('vehicles:detail.live.observedAt', { when: formatters.dateTime(state.ts) })
        }
        action={
          live?.state?.buffered ? (
            <Badge tone="warning">{t('vehicles:detail.live.buffered')}</Badge>
          ) : null
        }
      />

      <CardContent className="space-y-5">
        {state === null ? <AbsenceNotice live={live} isLoading={isLoading} /> : null}

        <div className="flex flex-wrap items-start justify-center gap-6 sm:justify-start">
          <ArcGauge
            label={t('vehicles:detail.live.speed')}
            metric={speed.value}
            max={speedScaleFor(vehicle)}
            redlineFrom={0.9}
            unit="km/h"
            format={(value) => formatters.integer(value)}
            hint={t(SPEED_SOURCE_HINT[speed.source])}
          />

          <ArcGauge
            label={t('vehicles:detail.live.rpm')}
            metric={rpm.metric}
            max={rpmScaleFor(vehicle)}
            redlineFrom={0.8}
            unit={t('vehicles:raw.unit.rpm')}
            format={(value) => formatters.integer(value)}
            hint={t('vehicles:detail.live.rpmHint')}
            // Rule 2: a dial the vehicle does not report renders its reason, not a
            // needle at zero. Zero rpm is a claim that the engine is off.
            pendingText={notice(rpm)}
          />

          <ArcGauge
            label={t('vehicles:detail.live.coolant')}
            metric={temperature.metric}
            min={TEMP_MIN}
            max={TEMP_MAX}
            redlineFrom={TEMP_REDLINE}
            unit="°C"
            format={(value) => formatters.integer(value)}
            hint={t('vehicles:detail.live.coolantHint')}
            pendingText={notice(temperature)}
          />

          <div className="min-w-[12rem] flex-1 space-y-3">
            <LevelGauge
              label={t('vehicles:detail.fuelLevel')}
              metric={
                fuelPct === null
                  ? capabilityReading(state, capabilities, 'fuel_level_pct').metric
                  : availableMetric(fuelPct)
              }
              format={(value) => `${formatters.integer(value)} %`}
              lowThreshold={15}
              secondary={fuelLitresText}
              pendingText={
                fuelPct === null
                  ? notice(capabilityReading(state, capabilities, 'fuel_level_pct'))
                  : undefined
              }
            />

            <SignalGauge
              label={t('vehicles:detail.live.gsm')}
              metric={channelReading(state, 'gsm_signal', 'vehicles:detail.live.gsmPending')}
              bars={5}
            />

            <SignalGauge
              label={t('vehicles:detail.live.gnss')}
              metric={
                state?.satellites === null || state?.satellites === undefined
                  ? pendingMetric('pending_telemetry')
                  : availableMetric(satelliteBars(state.satellites))
              }
              bars={5}
              valueText={
                state?.satellites === null || state?.satellites === undefined
                  ? undefined
                  : t('vehicles:detail.live.satellites', { satellites: state.satellites })
              }
              hint={
                state === null
                  ? undefined
                  : t(
                      state.gpsValid
                        ? 'vehicles:detail.live.fixValid'
                        : 'vehicles:detail.live.fixInvalid',
                    )
              }
            />
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-t border-border pt-4 lg:grid-cols-5">
          <IgnitionReading state={state} />
          <DoorReading state={state} />
          <VoltageReading
            icon={Zap}
            label={t('vehicles:detail.live.externalVoltage')}
            metric={channelReading(
              state,
              'external_voltage',
              'vehicles:detail.live.voltagePending',
            )}
          />
          <VoltageReading
            icon={Zap}
            label={t('vehicles:detail.live.batteryVoltage')}
            metric={channelReading(state, 'battery_voltage', 'vehicles:detail.live.voltagePending')}
          />
          {/* The calculateur's own supply rail, and CAN-only. It is the reading that
              separates "the tracker is browning out" from "the vehicle is". */}
          <ChannelVoltageReading
            label={t('vehicles:detail.live.controlModuleVoltage')}
            reading={capabilityReading(state, capabilities, 'control_module_voltage')}
            notice={notice}
          />
        </dl>

        <OdometerComparison state={state} capabilities={capabilities} />

        <CurrentTrip vehicle={vehicle} state={state} />

        {/* Rule 6: never a blank panel. If the bus is silent the dials above have
            already said so one by one; this is the sentence that names the fix. */}
        {!canAvailable && capabilities !== undefined ? (
          <CanUnavailableNotice profile={capabilities} />
        ) : null}
      </CardContent>
    </Card>
  )
}

type SpeedSource = 'can' | 'obd' | 'gps'

const SPEED_SOURCE_HINT: Record<SpeedSource, string> = {
  can: 'vehicles:detail.live.speedFromCan',
  obd: 'vehicles:detail.live.speedFromObd',
  gps: 'vehicles:detail.live.speedFromGps',
}

function speedMetric(state: LiveVehicleState | null): {
  value: MetricValue<number>
  source: SpeedSource
} {
  // The vehicle's own speedometer wins when it can be read — over CAN, else through the
  // OBD socket: GNSS speed is a derivative of position and lags a hard brake by a sample.
  const can = state?.channels?.vehicle_speed_can ?? null
  if (can !== null) return { value: availableMetric(can), source: 'can' }
  const obd = state?.channels?.obd_vehicle_speed ?? null
  if (obd !== null) return { value: availableMetric(obd), source: 'obd' }
  const gps = state?.speedKph ?? null
  return {
    value: gps === null ? pendingMetric('pending_telemetry') : availableMetric(gps),
    source: 'gps',
  }
}

/**
 * Engine RPM from the CAN adapter, else from the OBD socket.
 *
 * The two are separate channels on purpose (avl-map.ts, ids 36/37/48): the capability
 * profile reads `engine_rpm` as proof of a CAN adapter. Falling back here is safe for
 * the same reason it is for temperature — both are revolutions per minute of the same
 * engine — and the CAN reading wins when both exist.
 */
function rpmReading(
  state: LiveVehicleState | null,
  capabilities: CapabilityProfile | undefined,
): ChannelReading {
  const can = capabilityReading(state, capabilities, 'engine_rpm')
  if (!isPending(can.metric)) return can
  const obd = capabilityReading(state, capabilities, 'obd_engine_rpm')
  return isPending(obd.metric) ? can : obd
}

/**
 * The engine temperature, from whichever of the two channels this program publishes.
 *
 * Falling back is safe here in a way it would not be for, say, fuel: both channels are
 * degrees Celsius of the same engine, and the dial says which one it read. Preferring
 * coolant is not arbitrary — it is the one an ALL-CAN300 publishes on most programs.
 */
function temperatureReading(
  state: LiveVehicleState | null,
  capabilities: CapabilityProfile | undefined,
): ChannelReading {
  const coolant = capabilityReading(state, capabilities, 'coolant_temperature')
  if (!isPending(coolant.metric)) return coolant
  const engine = capabilityReading(state, capabilities, 'engine_temperature')
  return isPending(engine.metric) ? coolant : engine
}

/**
 * The two odometers, side by side, never merged — Phase 4 rule 6.
 *
 * The GNSS figure is integrated from position fixes and drifts under canopy; the CAN
 * figure is the vehicle's own dashboard counter and is what a service book is written
 * against. Overwriting one with the other loses the only signal that either is wrong,
 * so both are shown and so is the gap between them.
 */
function OdometerComparison({
  state,
  capabilities,
}: {
  state: LiveVehicleState | null
  capabilities?: CapabilityProfile
}) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const notice = useChannelNotice(capabilities)
  const pendingLabel = usePendingLabel()

  const gnssKm = state?.odometerKm ?? null
  // `total_mileage_can` is METRES on the wire. A kilometre figure a thousand times too
  // large stays plausible on a truck and is exactly the error that survives review.
  const canReading = capabilityReading(state, capabilities, 'total_mileage_can')
  const canKm = isPending(canReading.metric) ? null : canReading.metric.value / 1000

  const driftKm = gnssKm === null || canKm === null ? null : canKm - gnssKm

  return (
    <div className="rounded-lg border border-border bg-surface-muted/40 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        {t('vehicles:detail.odometer.title')}
      </p>

      <div className="mt-2 grid gap-4 sm:grid-cols-3">
        <div className="min-w-0">
          <p className="text-xs text-foreground-subtle">{t('vehicles:detail.odometer.gnss')}</p>
          <p
            className={cn(
              'text-sm tabular-nums',
              gnssKm === null ? 'text-foreground-subtle' : 'font-semibold text-foreground',
            )}
          >
            {gnssKm === null
              ? pendingLabel(pendingMetric('pending_telemetry'))
              : `${formatters.integer(gnssKm)} ${t('common:units.km')}`}
          </p>
        </div>

        <div className="min-w-0">
          <p className="text-xs text-foreground-subtle">{t('vehicles:detail.odometer.can')}</p>
          <p
            className={cn(
              'text-sm tabular-nums',
              canKm === null ? 'text-foreground-subtle' : 'font-semibold text-foreground',
            )}
          >
            {canKm === null
              ? (notice(canReading) ?? t('vehicles:empty.noCanData.title'))
              : `${formatters.integer(canKm)} ${t('common:units.km')}`}
          </p>
        </div>

        <div className="min-w-0">
          <p className="text-xs text-foreground-subtle">{t('vehicles:detail.odometer.drift')}</p>
          <p className="text-sm tabular-nums text-foreground-muted">
            {driftKm === null
              ? t('vehicles:detail.odometer.driftUnavailable')
              : `${driftKm > 0 ? '+' : ''}${formatters.integer(driftKm)} ${t('common:units.km')}`}
          </p>
        </div>
      </div>
    </div>
  )
}

function AbsenceNotice({ live, isLoading }: { live?: LiveVehicleEnvelope; isLoading: boolean }) {
  const { t } = useTranslation()
  if (isLoading) return null

  // Four absences, four different support tickets. `projection_expired` in
  // particular is NOT a fault: the platform has heard from the vehicle and the
  // cache simply does not hold it, so the next record restores it.
  //
  // No envelope at all means the request itself failed, which is OUR outage and
  // not the customer's tracker. Reporting that as "never transmitted" would send
  // an installer to a truck whose device is working perfectly.
  const reason =
    live === undefined ? 'projection_unavailable' : (live.absenceReason ?? 'never_reported')
  return (
    <div className="rounded-lg border border-dashed border-border bg-surface-muted/50 p-3">
      <p className="text-sm font-semibold text-foreground">
        {t(`vehicles:absence.${reason}.title`)}
      </p>
      <p className="mt-1 text-sm leading-relaxed text-foreground-muted">
        {t(`vehicles:absence.${reason}.description`)}
      </p>
    </div>
  )
}

function IgnitionReading({ state }: { state: LiveVehicleState | null }) {
  const { t } = useTranslation()
  const on = state?.ignition ?? null
  const confidence = state?.ignitionConfidence ?? 'unknown'

  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        <KeyRound aria-hidden className="h-3.5 w-3.5" />
        {t('vehicles:detail.live.ignition')}
      </dt>
      <dd className="mt-1 flex flex-wrap items-center gap-1.5">
        {on === null ? (
          <span className="text-sm text-foreground-subtle">
            {t('common:metric.pendingTelemetry')}
          </span>
        ) : (
          <Badge tone={on ? 'success' : 'neutral'}>
            {t(on ? 'vehicles:raw.logic.on' : 'vehicles:raw.logic.off')}
          </Badge>
        )}
        {/* 'inferred' has to reach the screen. On an OBD install there is no
            ignition wire, so the edges are reconstructed from voltage and motion
            and a trip may start a minute late — presenting that as measured is
            what turns a soft boundary into a disputed timesheet. */}
        {confidence !== 'unknown' ? (
          <Badge tone={confidence === 'wired' ? 'neutral' : 'warning'}>
            {t(`vehicles:detail.live.ignitionConfidence.${confidence}`)}
          </Badge>
        ) : null}
      </dd>
    </div>
  )
}

function DoorReading({ state }: { state: LiveVehicleState | null }) {
  const { t } = useTranslation()
  const mask = state?.channels?.door_status ?? null
  const bits = mask === null ? [] : setBits(mask)

  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        <DoorOpen aria-hidden className="h-3.5 w-3.5" />
        {t('vehicles:detail.live.doors')}
      </dt>
      <dd className="mt-1">
        {mask === null ? (
          <span className="text-sm text-foreground-subtle">
            {t('vehicles:empty.noCanData.title')}
          </span>
        ) : bits.length === 0 ? (
          <Badge tone="success">{t('vehicles:detail.live.doorsClosed')}</Badge>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge tone="warning">
              {t('vehicles:detail.live.doorsOpen', { doors: bits.length })}
            </Badge>
            {/* The bit-to-door mapping is a property of the CAN program number,
                not of the protocol, so the bits are named and not guessed at. */}
            <span className="text-xs text-foreground-subtle">
              {t('vehicles:detail.live.doorBits', { bits: bits.join(', ') })}
            </span>
          </div>
        )}
      </dd>
    </div>
  )
}

function VoltageReading({
  icon: Icon,
  label,
  metric,
}: {
  icon: LucideIcon
  label: string
  metric: MetricValue<number>
}) {
  const formatters = useFormatters()
  const pendingLabel = usePendingLabel()
  const pending = isPending(metric)

  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        <Icon aria-hidden className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd
        className={cn(
          'mt-1 text-sm tabular-nums',
          pending ? 'text-foreground-subtle' : 'font-medium text-foreground',
        )}
      >
        {pending ? pendingLabel(metric) : `${formatters.decimal(metric.value, 2)} V`}
      </dd>
    </div>
  )
}

/**
 * A voltage that comes off the CAN bus rather than off the tracker's own terminals.
 *
 * Same layout as <VoltageReading />, different absence: this one can say "the bus
 * stopped reporting on 14 March" where the tracker's own rails can only ever say the
 * device is silent.
 */
function ChannelVoltageReading({
  label,
  reading,
  notice,
}: {
  label: string
  reading: ChannelReading
  notice: (reading: ChannelReading) => string | undefined
}) {
  const formatters = useFormatters()
  const pendingLabel = usePendingLabel()
  const pending = isPending(reading.metric)

  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-foreground-subtle">
        <Zap aria-hidden className="h-3.5 w-3.5" />
        {label}
      </dt>
      <dd
        className={cn(
          'mt-1 text-sm tabular-nums',
          pending ? 'text-foreground-subtle' : 'font-medium text-foreground',
        )}
      >
        {isPending(reading.metric)
          ? (notice(reading) ?? pendingLabel(reading.metric))
          : `${formatters.decimal(reading.metric.value, 2)} V`}
      </dd>
    </div>
  )
}

/**
 * The trip so far.
 *
 * Phase 2 has no trips endpoint, so this reads the device's own trip odometer (and
 * the CAN worktime counter for a machine's work session) rather than inventing a
 * trip. Duration is left explicitly pending on phase 3 instead of being computed
 * from the newest record's timestamp, which would report the age of the fix and
 * call it a trip length.
 */
function CurrentTrip({ vehicle, state }: { vehicle: VehicleView; state: LiveVehicleState | null }) {
  const { t } = useTranslation()
  const formatters = useFormatters()
  const pendingLabel = usePendingLabel()
  const machine = isMachine(vehicle)

  const inTrip = state?.ignition === true || state?.movementState === 'moving'

  const reading: MetricValue<number> = machine
    ? channelReading(state, 'engine_worktime')
    : tripDistance(state)

  const duration = pendingMetric('pending_telemetry', {
    reasonKey: 'vehicles:detail.trip.durationPending',
  })

  return (
    <div className="rounded-lg border border-border bg-surface-muted/40 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-foreground-subtle">
          {t(machine ? 'vehicles:detail.trip.session' : 'vehicles:detail.trip.current')}
        </p>
        {state !== null && !inTrip ? (
          <Badge tone="neutral">{t('vehicles:detail.trip.notInTrip')}</Badge>
        ) : null}
      </div>

      <div className="mt-2 grid grid-cols-2 gap-4">
        <div className="min-w-0">
          <p className="text-xs text-foreground-subtle">
            {t(machine ? 'vehicles:detail.trip.worktime' : 'vehicles:detail.trip.distance')}
          </p>
          <p
            className={cn(
              'text-sm tabular-nums',
              isPending(reading) ? 'text-foreground-subtle' : 'font-semibold text-foreground',
            )}
          >
            {isPending(reading)
              ? pendingLabel(reading)
              : machine
                ? formatters.duration(reading.value * 60)
                : `${formatters.decimal(reading.value, 1)} ${t('common:units.km')}`}
          </p>
        </div>

        <div className="min-w-0">
          <p className="text-xs text-foreground-subtle">{t('vehicles:detail.trip.duration')}</p>
          <p className="text-sm text-foreground-subtle">{pendingLabel(duration)}</p>
        </div>
      </div>
    </div>
  )
}

/** `trip_odometer` is METRES on the wire; a kilometre figure 1000× too large is
 *  the kind of error that survives review because it is still a plausible number. */
function tripDistance(state: LiveVehicleState | null): MetricValue<number> {
  const metres = state?.channels?.trip_odometer ?? null
  if (metres === null) {
    return state === null
      ? pendingMetric('pending_telemetry')
      : pendingMetric('not_supported_by_vehicle', {
          reasonKey: 'vehicles:detail.trip.noTripOdometer',
        })
  }
  return availableMetric(metres / 1000)
}
